import { Inject } from '@odinjs/odin'
import BaseController from '../../../BaseController';
import Controller, { OnClick, OnMount, OnReadSuccess } from '@philips-internal/oswald-ui-core/lib/client';
import { FragmentNames, OralDietExecuteTimeModalAliases } from '../../../../codegen';
import { ClickEvent, MountEvent, ReadEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { BarcodeAPI, BarcodeData, ButtonAPI, DeckAPI, DeckValue, FieldAPI, FormAPI, ModalService, PanelAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { HttpService,  } from '@philips-internal/oswald-ui-core/lib/services';
import moment from 'moment';
import { Endpoint } from '../../../../constants/endpoints';
import { FoundationUtils } from '../../../../utils/foundation-utils';
import { ADEP_E1113Parameters, ADEP_E9129Parameters } from '../../../../utils/parameters';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import MedicationService, { ConsistAdministrationMarginInfoRequest, CreatePrescriptionScheduleChangeRequest, CreatePrescriptionScheduleChangeResponse } from '../../services/medication-service';
import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import TimelineContext, { TimeLineEventMedication } from '../../services/timeline-service';
import { ApplicationRoute } from '../../../../constants/routes';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { BarcodeUtils } from '../../../../utils/barcode-utils';
import BarcodeService from '../../../../services/barcode-service';
import { Function1113Param13Values } from '../oral-supplements-viewer/oral-supplements-params';
import { YesNoType } from '../../../../utils/enum-utils';

const FUNCTION_1113 = 'ADEP#1113';

@Controller({ fragment: FragmentNames.ORAL_DIET_EXECUTE_TIME_MODAL })
export default class OralDielExecuteTimeModalController extends BaseController {

  @Inject(OralDietExecuteTimeModalAliases.ORAL_DIET_DESCRIPTION_DECK)
  private oralDietDeckApi!: DeckAPI;

  @Inject(OralDietExecuteTimeModalAliases.DIET_JUSTIFICATION_FORM)
  private oralDietFormApi!: FormAPI;

  @Inject(OralDietExecuteTimeModalAliases.EXECUTE_BUTTON)
  private executeButtonApi!: ButtonAPI;

  @OnMount(OralDietExecuteTimeModalAliases.EXECUTE_TIME_BAR_CODE_PANEL)
  onMountBarcode(event: MountEvent): void {
    const barcodePanel = event.target as PanelAPI;
    HttpService.get<boolean>(Endpoint.EMAR_ORAL_DIET_VERIFY_GENERATE_PROCESS_DIET)
      .then(param => {
        const valid = Boolean(param.body) && this.validateMedicationBeforeAdmin();
        this.executeButtonApi.setEnabled(!valid);
        barcodePanel.setVisible(valid);
      });
  }

  @OnMount(OralDietExecuteTimeModalAliases.ORAL_DIET_DESCRIPTION_DECK)
  onMountDeck(): void {
    const { barCode, information } = this.context.getBulkData();

    const dietInformation = {
      barcode: barCode,
      materialDescription: information,
      read: false,
    };

    this.oralDietDeckApi.setData([ dietInformation as DeckValue ]);
    this.executeButtonApi.setEnabled(false);
  }

  validateMedicationBeforeAdmin(): boolean {
    const tasyFunctionName = 'ADEP#9129';
    const param3 = FoundationUtils.findModuleParam(tasyFunctionName, ADEP_E9129Parameters.CHECK_MEDICATIONS_BEFORE_ADMIN);
    return 'S' === param3
  }

  @OnMount(OralDietExecuteTimeModalAliases.EXECUTE_TIME_BAR_CODE)
  handleMountBarcode(event: MountEvent): void {
    const barcodeApi = event.target as BarcodeAPI
    barcodeApi.setDefaultReadType(BarcodeService.getDefaultMethodOfReadingBarcode())
  }

  @OnReadSuccess(OralDietExecuteTimeModalAliases.EXECUTE_TIME_BAR_CODE)
  onReadBarcode(event: ReadEvent<BarcodeData>): void {
    const barcodeApi = event.target as BarcodeAPI;
    barcodeApi.close();

    const barCodeFromDiet = String(this.context.get('barCode'));
    const barcodeValue = String(event.item.value.trim());

    if (barCodeFromDiet === barcodeValue) {
      this.setBarcodeReadStatus(this.oralDietDeckApi, barcodeValue, true);
    } else {
      this.showToast('text.label-number-does-not-match-error', ToastLevel.WARNING, ToastType.BANNER)
    }
  }

  @OnClick(OralDietExecuteTimeModalAliases.ADMINISTRATION_BY_PROCESS_CARD)
  onDisaposeClickEvent(event: ClickEvent): void {
    const barcode = event.value.barcode;
    if (barcode) {
      BarcodeUtils.dialogModalConfirm(barcode, () => this.disaposeReadStatus(barcode));
    }
  }

  disaposeReadStatus(barcode: string): void {
    this.setBarcodeReadStatus(this.oralDietDeckApi, barcode, false);
  }

  @OnMount(OralDietExecuteTimeModalAliases.DIET_DESCRIPTION_FIELD)
  onMountDescriptionField(event: MountEvent): void {
    const field = event.target as FieldAPI;
    const dietDescription = this.context.get('dietDescription')
    field.setData({ dietDescription });
  }

  @OnMount(OralDietExecuteTimeModalAliases.DIET_OBSERVATION_FIELD)
  onMountObservationField(event: MountEvent): void {
    const field = event.target as FieldAPI;
    const informationDescription = this.context.get('informationDescription');
    field.setData({ informationDescription });
  }

  @OnMount(OralDietExecuteTimeModalAliases.DIET_HOUR_FIELD)
  onMountScheduleDateField(event: MountEvent): void {
    const field = event.target as FieldAPI;
    const scheduleDate = this.context.get('scheduleDate');
    const formatedDate = moment(String(scheduleDate)).format('L LTS')
    field.setData({ scheduleDate: formatedDate });
  }

  @OnClick(OralDietExecuteTimeModalAliases.EXECUTE_BUTTON)
  onClickExecuteButton(): void {
    const { justification } = this.oralDietFormApi.getRecord();
    const { scheduleSequenceNumber } = this.historyService.getCurrentRouteParams();
    const dateTimeCurrent = moment().format(ISO_FULL_DATE_FORMAT);
    const param13 = FoundationUtils.findModuleParam(FUNCTION_1113, ADEP_E1113Parameters.ACTION_TO_BE_TAKEN_IN_CASE_OF_EXTRAPOLATION_OF_THE_CHECKING_MARGIN) as string;
    const param507 = FoundationUtils.findModuleParam(FUNCTION_1113, ADEP_E1113Parameters.ACTION_IN_CASE_EXTRAPOLATION_CHECKING_MARGIN_PRIOR_START) as number | undefined;
    const param12 = FoundationUtils.findModuleParam(FUNCTION_1113, ADEP_E1113Parameters.MARGIN_IN_MINUTES_CHECKING_OUTSIDE_PRESCRIBED_HOURS) as number;
    const justificationRequired = param13 === Function1113Param13Values.REQUIRED_JUSTIFICATION || param13 === Function1113Param13Values.NOT_ALLOWED_AND_jUSTIFICATION;
    const requiresJustificationStatus =  justificationRequired ? YesNoType.YES : YesNoType.NO;

    const body: ConsistAdministrationMarginInfoRequest = {
      actionAdministrationOutsideMarginStatus: param13,
      eventDate: dateTimeCurrent,
      hasAsEvent: justification ? 'S' : 'N',
      itemType: 'D',
      justificationDescription: justification ? String(justification) : '',
      minutesMarginBefore: param507 ?? 0,
      minutesQuantityMarginAdministration: param12,
      requiresJustificationStatus: requiresJustificationStatus,
      scheduleSequence: Number(scheduleSequenceNumber),
    };

    this.isLoading(true);

    this.validateWithParam13(param13)
      .then(() => MedicationService.consistAdministrationMarginInfoResponse(body))
      .then(response => {
        if(response.responseDescription) {
          this.showToast(response.responseDescription, ToastLevel.WARNING, ToastType.BANNER);
          return Promise.reject(false);
        }
        return this.createPrescription();
      })
      .then(() => {
        this.isLoading(false)
        this.showToast('tws.emar.administered-item', ToastLevel.SUCCESS, ToastType.BANNER);

        this.redirectUserToTimeline();
      })
      .catch(error => {
        this.isLoading(false);
        const hasError = !!error;

        if (hasError) {
          this.redirectUserToTimeline();
          if (error.body?.errors) {
            error?.body?.errors.forEach((message: string) =>
              this.showToast(message, ToastLevel.WARNING, ToastType.BANNER))
            return;
          }

          if (error.body) {
            this.showToast(error.body, ToastLevel.ERROR, ToastType.BANNER);
          }
        }
      })
  }

  redirectUserToTimeline(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;
    applicationContext.redirect(redirectRoute, { encounterId, patientId });
    applicationContext.clearNavigationStack();
    ModalService.close(FragmentNames.ORAL_DIET_EXECUTE_TIME_MODAL);
  }

  validateWithParam13(param13: string): Promise<boolean>{
    return new Promise((resolve) => resolve(param13 === 'EJ'));
  }

  createPrescription(): Promise<CreatePrescriptionScheduleChangeResponse> {
    const { encounterId, scheduleSequenceNumber } = this.historyService.getCurrentRouteParams();
    const dateTimeCurrent = moment().format(ISO_FULL_DATE_FORMAT);
    const timelineMedication = TimelineContext.get() as TimeLineEventMedication;
    const param22 = FoundationUtils.findModuleParam(FUNCTION_1113, ADEP_E1113Parameters.PRESCRIPTION_HOUR_QUANTITY) as number;
    const { intervalCode, prescriptionNumber, informationDescription, sequenceDietNumber } = this.context.getBulkData();
    const { justification } = this.oralDietFormApi.getRecord();

    const body: CreatePrescriptionScheduleChangeRequest = {
      administrationRouteType: '',
      componentsDescription: '',
      encounterNumber: Number(encounterId),
      eventDate: dateTimeCurrent,
      functionCode: 9129,
      groupingNumber: 0,
      intervalCode: String(intervalCode),
      isGenerateAll: '',
      itemCode: String(timelineMedication.itemCode),
      itemDescription: String(timelineMedication.description),
      itemObservationDescription: '',
      itemQuantity: 0,
      itemSequence: Number(sequenceDietNumber),
      itemType: 'D',
      justifyDescription: justification ? String(justification) : '',
      messageDescriptionResponse: '',
      observationDescription: informationDescription ? String(informationDescription) : '',
      pdoPrn: 'N',
      prescriptionHourQuantity: param22,
      prescriptionNumber: Number(prescriptionNumber),
      prescriptionsNumber: String(prescriptionNumber),
      scheduleSequence: Number(scheduleSequenceNumber),
      suspendPrescribedMaterial: '',
      unitMeasureCode: '',
      uoMCodeQuantityAdministered: '',
    }
    return MedicationService.createPrescriptionScheduleChange(body)
  }

  private setBarcodeReadStatus(deck: DeckAPI, barcodeValue: string, read: boolean): void {
    const updatedData = deck.getData().map(value => {
      if (value.barcode === barcodeValue) {
        return {
          ...value,
          read
        };
      }
      return value;
    });
    const hasUnreadBarcode = updatedData.some(value => value.read === false);
    this.executeButtonApi.setEnabled(!hasUnreadBarcode);
    deck.setData(updatedData);
  }

  private showToast(message: string, toastLevel: ToastLevel, type: ToastType): void {
    const toastId = 'oral-diet-execute-time-toast';
    ToastService.close(toastId);
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: toastId,
      level: toastLevel,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type,
    });
  }

  private isLoading(state: boolean): void {
    this.executeButtonApi.setEnabled(!state);
    this.oralDietFormApi.setLoading(state);
  }
}

